import { useCallback, useMemo } from 'react';
import { useGooglePlaces } from './google';
import type { Place, VenueValues } from './types';
import { VenueType } from './types';
import { useVenues } from './venues';

export const createGoogleSuggestion = (
    suggestion: google.maps.places.AutocompletePrediction,
): Place => ({
    value: suggestion['place_id'],
    content: suggestion.description,
    data: suggestion,
    type: VenueType.Google,
});

export const createVenueSuggestion = (venue: VenueValues): Place => ({
    value: venue.id,
    content: venue.name,
    data: venue,
    type: VenueType.Venue,
});

export const useVenueSearch = ({
    organizationId,
    blacklistedCountries,
}: {
    organizationId: string;
    blacklistedCountries?: Set<string>;
}) => {
    const { venues, filter } = useVenues({
        organizationId,
        blacklistedCountries,
    });
    const { places, search, getDetails } = useGooglePlaces({
        blacklistedCountries,
    });

    const suggestions = useMemo(
        () => [
            ...venues.map(createVenueSuggestion),
            ...places.map(createGoogleSuggestion),
        ],
        [venues, places],
    );

    const getSuggestions = useCallback(
        (query: string) => {
            filter(query);
            search(query);
        },
        [filter, search],
    );

    const getSuggestion = useCallback(
        async ({
            placeId,
        }: {
            placeId: string;
        }): Promise<google.maps.places.PlaceResult | null> => {
            const place = await getDetails(placeId);
            return place;
        },
        [getDetails],
    );

    return {
        suggestions,
        getSuggestions,
        getSuggestion,
    };
};
